import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ClipboardList,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Phone,
  PlugZap,
  Plus,
  Power,
  QrCode,
  RefreshCw,
  Save,
  Send,
  Server,
  Settings,
  Trash2,
  Wifi
} from 'lucide-react';
import {
  evolutionWhatsAppService,
  EvolutionInstance,
  getEvolutionConnectionLabel,
  getEvolutionInstanceOwnerNumber,
  getEvolutionInstanceName,
  getEvolutionInstanceState,
  isEvolutionConnected,
  WhatsAppBackendConfig
} from '../../services/evolutionWhatsAppService';
import { contractService } from '../../services/contractService';
import { notificationService } from '../../services/notificationService';
import {
  DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS,
  WhatsAppNotificationSettings,
  whatsappNotificationSettingsService
} from '../../services/whatsappNotificationSettingsService';
import { Contract } from '../../types/contract';

const DEFAULT_INSTANCE = 'playkids';

const formatQrImage = (qrCode: string) => {
  if (!qrCode) return '';
  if (qrCode.startsWith('data:image')) return qrCode;
  if (qrCode.startsWith('http')) return qrCode;
  return `data:image/png;base64,${qrCode}`;
};

const readStatusText = (status: unknown) => {
  if (!status) return 'Sem retorno';
  if (typeof status === 'string') return getEvolutionConnectionLabel(status);
  if (typeof status === 'object') {
    const value = status as { instance?: { state?: string }; state?: string };
    const state = value.instance?.state || value.state;
    return state ? getEvolutionConnectionLabel(state) : JSON.stringify(status);
  }
  return String(status);
};

const readRawState = (status: unknown) => {
  if (!status) return '';
  if (typeof status === 'string') return status;
  if (typeof status === 'object') {
    const value = status as { instance?: { state?: string }; state?: string };
    return value.instance?.state || value.state || '';
  }
  return String(status);
};

const WhatsAppConnectionPage: React.FC = () => {
  const [backendUrl, setBackendUrl] = useState(evolutionWhatsAppService.getBackendUrl());
  const [backendToken, setBackendToken] = useState(evolutionWhatsAppService.getToken());
  const [config, setConfig] = useState<WhatsAppBackendConfig | null>(null);
  const [instances, setInstances] = useState<EvolutionInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState(DEFAULT_INSTANCE);
  const [instanceName, setInstanceName] = useState(DEFAULT_INSTANCE);
  const [qrCode, setQrCode] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [pairingPhoneNumber, setPairingPhoneNumber] = useState('');
  const [statusText, setStatusText] = useState('');
  const [loading, setLoading] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'connection' | 'tests' | 'settings'>('connection');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [testNumber, setTestNumber] = useState('');
  const [testResult, setTestResult] = useState('');
  const [notificationSettings, setNotificationSettings] = useState<WhatsAppNotificationSettings>(DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS);

  const selectedState = useMemo(() => {
    const current = instances.find((instance) => getEvolutionInstanceName(instance) === selectedInstance);
    return current ? getEvolutionInstanceState(current) : '';
  }, [instances, selectedInstance]);

  const selectedInstanceData = useMemo(() => {
    return instances.find((instance) => getEvolutionInstanceName(instance) === selectedInstance);
  }, [instances, selectedInstance]);

  const connectedInstanceNumber = useMemo(() => {
    return selectedInstanceData ? getEvolutionInstanceOwnerNumber(selectedInstanceData) : '';
  }, [selectedInstanceData]);

  const notificationInstanceState = useMemo(() => {
    const current = instances.find((instance) => getEvolutionInstanceName(instance) === notificationSettings.instanceName);
    return current ? getEvolutionInstanceState(current) : '';
  }, [instances, notificationSettings.instanceName]);

  const selectedContract = useMemo(() => {
    return contracts.find((contract) => contract.id === selectedContractId) || null;
  }, [contracts, selectedContractId]);

  const testMessage = useMemo(() => {
    return selectedContract ? notificationService.buildNewContractMessage(selectedContract) : '';
  }, [selectedContract]);

  const showNotice = (type: 'success' | 'error' | 'info', text: string) => {
    setNotice({ type, text });
  };

  const loadAll = async (options: { silent?: boolean } = {}) => {
    if (!options.silent) setLoading('load');
    try {
      setBackendUrl(evolutionWhatsAppService.getBackendUrl());
      const [backendConfig, list] = await Promise.all([
        evolutionWhatsAppService.getConfig(),
        evolutionWhatsAppService.listInstances()
      ]);
      setConfig(backendConfig);
      setInstances(list);

      const firstName = list.map(getEvolutionInstanceName).find(Boolean);
      if (firstName && selectedInstance === DEFAULT_INSTANCE) {
        setSelectedInstance(firstName);
        setInstanceName(firstName);
      }
      if (!options.silent) showNotice('success', 'Conexao com o backend verificada.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel carregar os dados.';
      if (!options.silent) {
        showNotice('error', message.includes('Failed to fetch') ? 'Nao foi possivel acessar o backend. Confira se a URL esta como https://playkids-whatsapp-evolution.fly.dev e clique em Salvar.' : message);
      }
    } finally {
      if (!options.silent) setLoading('');
    }
  };

  const loadContracts = async () => {
    setLoading('contracts');
    try {
      const list = await contractService.getContracts();
      setContracts(list);
      if (!selectedContractId && list[0]?.id) {
        setSelectedContractId(list[0].id);
      }
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Nao foi possivel carregar as inscricoes.');
    } finally {
      setLoading('');
    }
  };

  const loadNotificationSettings = async () => {
    setLoading('settings');
    try {
      const settings = await whatsappNotificationSettingsService.getSettings();
      setNotificationSettings(settings);
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Nao foi possivel carregar as configuracoes.');
    } finally {
      setLoading('');
    }
  };

  useEffect(() => {
    loadAll();
    loadContracts();
    loadNotificationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveBackendSettings = () => {
    evolutionWhatsAppService.setBackendUrl(backendUrl);
    evolutionWhatsAppService.setToken(backendToken);
    showNotice('info', 'Configuracao salva neste navegador.');
    loadAll();
  };

  const createInstance = async () => {
    setLoading('create');
    setQrCode('');
    setPairingCode('');
    try {
      const qr = await evolutionWhatsAppService.createInstance(instanceName);
      setSelectedInstance(instanceName);
      setQrCode(qr.qrCode);
      setPairingCode(qr.pairingCode);
      showNotice('success', qr.qrCode || qr.pairingCode ? 'Instancia pronta. Leia o QR Code ou use o codigo para conectar.' : 'Instancia pronta. Clique em QR Code para atualizar o pareamento.');
      await loadAll();
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao criar instancia.');
    } finally {
      setLoading('');
    }
  };

  const connectInstance = async () => {
    setLoading('connect');
    setQrCode('');
    setPairingCode('');
    try {
      const qr = await evolutionWhatsAppService.connectInstance(selectedInstance);
      setQrCode(qr.qrCode);
      setPairingCode(qr.pairingCode);
      showNotice('success', 'QR Code atualizado.');
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao conectar instancia.');
    } finally {
      setLoading('');
    }
  };

  const connectWithPhoneNumber = async () => {
    const digits = pairingPhoneNumber.replace(/\D/g, '');
    if (!digits) {
      showNotice('error', 'Informe o numero com DDI + DDD. Ex: 5533999999999.');
      return;
    }

    setLoading('connect-number');
    setQrCode('');
    setPairingCode('');
    try {
      const qr = await evolutionWhatsAppService.connectInstance(selectedInstance, digits);
      setQrCode(qr.qrCode);
      setPairingCode(qr.pairingCode);
      if (qr.pairingCode) {
        showNotice('success', 'Codigo de pareamento gerado. Insira-o no WhatsApp em Aparelhos conectados > Conectar com numero de telefone.');
      } else {
        showNotice('error', 'O backend nao retornou um codigo de pareamento para esse numero.');
      }
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao gerar codigo de pareamento.');
    } finally {
      setLoading('');
    }
  };

  const refreshStatus = async (options: { silent?: boolean } = {}) => {
    if (!options.silent) setLoading('status');
    try {
      const status = await evolutionWhatsAppService.getStatus(selectedInstance);
      const rawState = readRawState(status);
      const label = readStatusText(status);
      setStatusText(label);
      if (isEvolutionConnected(rawState)) {
        setQrCode('');
        setPairingCode('');
        showNotice('success', 'WhatsApp conectado com sucesso.');
      } else if (!options.silent) {
        showNotice('success', 'Status atualizado.');
      }
      await loadAll({ silent: options.silent });
    } catch (error) {
      if (!options.silent) {
        showNotice('error', error instanceof Error ? error.message : 'Erro ao consultar status.');
      }
    } finally {
      if (!options.silent) setLoading('');
    }
  };

  useEffect(() => {
    if ((!qrCode && !pairingCode) || !selectedInstance) return;

    const interval = window.setInterval(() => {
      refreshStatus({ silent: true });
    }, 5000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, pairingCode, selectedInstance]);

  const logoutInstance = async () => {
    if (!selectedInstance) return;
    setLoading('logout');
    try {
      await evolutionWhatsAppService.logoutInstance(selectedInstance);
      setQrCode('');
      showNotice('success', 'Instancia desconectada.');
      await loadAll();
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao desconectar instancia.');
    } finally {
      setLoading('');
    }
  };

  const deleteInstance = async () => {
    if (!selectedInstance) return;
    const confirmed = window.confirm(`Excluir a instancia "${selectedInstance}"?`);
    if (!confirmed) return;

    setLoading('delete');
    try {
      await evolutionWhatsAppService.deleteInstance(selectedInstance);
      setSelectedInstance(DEFAULT_INSTANCE);
      setInstanceName(DEFAULT_INSTANCE);
      setQrCode('');
      showNotice('success', 'Instancia excluida.');
      await loadAll();
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao excluir instancia.');
    } finally {
      setLoading('');
    }
  };

  const sendTestContract = async () => {
    if (!selectedContract) {
      showNotice('error', 'Selecione uma inscricao para enviar o teste.');
      return;
    }

    setLoading('send-test');
    setTestResult('');
    try {
      const result = await evolutionWhatsAppService.sendTestMessage(
        selectedInstance,
        testMessage,
        testNumber || connectedInstanceNumber
      );
      setTestResult(`Mensagem enviada para ${result.number}.`);
      showNotice('success', 'Teste enviado pela instancia WhatsApp.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao enviar teste.';
      setTestResult(message);
      showNotice('error', message);
    } finally {
      setLoading('');
    }
  };

  const addDestinationNumber = () => {
    setNotificationSettings((current) => ({
      ...current,
      destinationNumbers: [...current.destinationNumbers, '']
    }));
  };

  const updateDestinationNumber = (index: number, value: string) => {
    setNotificationSettings((current) => ({
      ...current,
      destinationNumbers: current.destinationNumbers.map((number, i) =>
        i === index ? value.replace(/\D/g, '') : number
      )
    }));
  };

  const removeDestinationNumber = (index: number) => {
    setNotificationSettings((current) => ({
      ...current,
      destinationNumbers: current.destinationNumbers.filter((_, i) => i !== index)
    }));
  };

  const saveNotificationSettings = async () => {
    setLoading('save-settings');
    try {
      await whatsappNotificationSettingsService.saveSettings(notificationSettings);
      showNotice('success', 'Configuracoes de envio automatico salvas.');
    } catch (error) {
      showNotice('error', error instanceof Error ? error.message : 'Erro ao salvar configuracoes.');
    } finally {
      setLoading('');
    }
  };

  const qrImage = formatQrImage(qrCode);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-emerald-600 flex items-center justify-center shadow-sm">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Conexao WhatsApp</h1>
              <p className="text-sm text-slate-600">Gerencie a instancia da Evolution API usada pelo sistema.</p>
            </div>
          </div>

          <button
            onClick={() => loadAll()}
            disabled={Boolean(loading)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 disabled:opacity-60"
          >
            {loading === 'load' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Atualizar
          </button>
        </div>

        {notice && (
          <div className={`rounded-lg border p-4 text-sm flex items-start gap-3 ${
            notice.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : notice.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            {notice.type === 'success' ? <CheckCircle2 className="w-5 h-5 mt-0.5" /> : <AlertCircle className="w-5 h-5 mt-0.5" />}
            <span>{notice.text}</span>
          </div>
        )}

        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('connection')}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${
              activeTab === 'connection' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <PlugZap className="w-4 h-4" />
            Conexao
          </button>
          <button
            onClick={() => setActiveTab('tests')}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${
              activeTab === 'tests' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Testes
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${
              activeTab === 'settings' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Settings className="w-4 h-4" />
            Configuracoes
          </button>
        </div>

        {activeTab === 'connection' && (
          <>
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <Server className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Backend</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px_120px] gap-3">
            <input
              value={backendUrl}
              onChange={(event) => setBackendUrl(event.target.value)}
              className="input"
              placeholder="https://seu-app.fly.dev"
            />
            <input
              value={backendToken}
              onChange={(event) => setBackendToken(event.target.value)}
              className="input"
              type="password"
              placeholder="Token do backend"
            />
            <button
              onClick={saveBackendSettings}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <span className="text-slate-500 block">Evolution API</span>
              <strong className="text-slate-900">{config?.configured ? 'Configurada' : 'Pendente'}</strong>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <span className="text-slate-500 block">Token do backend</span>
              <strong className="text-slate-900">{config?.authEnabled ? 'Ativo' : 'Desativado'}</strong>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <span className="text-slate-500 block">Instancias</span>
              <strong className="text-slate-900">{instances.length}</strong>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
          <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <Plus className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-semibold text-slate-900">Nova instancia</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Nome da instancia</label>
                <input
                  value={instanceName}
                  onChange={(event) => setInstanceName(event.target.value)}
                  className="input"
                  placeholder="playkids"
                />
              </div>
              <button
                onClick={createInstance}
                disabled={Boolean(loading)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {loading === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar e gerar QR Code
              </button>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-5">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Instancia selecionada</label>
              <select
                value={selectedInstance}
                onChange={(event) => setSelectedInstance(event.target.value)}
                className="input"
              >
                <option value={DEFAULT_INSTANCE}>{DEFAULT_INSTANCE}</option>
                {instances.map((instance) => {
                  const name = getEvolutionInstanceName(instance);
                  return name ? <option key={name} value={name}>{name}</option> : null;
                })}
              </select>
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                <Wifi className="w-4 h-4" />
                <span>Status listado: {selectedState ? getEvolutionConnectionLabel(selectedState) : 'nao encontrado'}</span>
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
              <div className="flex items-center gap-3">
                <PlugZap className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-slate-900">Conectar aparelho</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={connectInstance}
                  disabled={Boolean(loading)}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {loading === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                  QR Code
                </button>
                <button
                  onClick={() => refreshStatus()}
                  disabled={Boolean(loading)}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 disabled:opacity-60"
                >
                  {loading === 'status' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Status
                </button>
                <button
                  onClick={logoutInstance}
                  disabled={Boolean(loading)}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  <Power className="w-4 h-4" />
                  Desconectar
                </button>
                <button
                  onClick={deleteInstance}
                  disabled={Boolean(loading)}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
              <div className="rounded-lg border border-slate-200 bg-slate-50 min-h-[280px] flex items-center justify-center p-4">
                {qrImage ? (
                  <img src={qrImage} alt="QR Code da instancia WhatsApp" className="w-full max-w-[240px] rounded-lg bg-white p-2 shadow-sm" />
                ) : (
                  <div className="text-center text-slate-500">
                    <QrCode className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Gere um QR Code para conectar o WhatsApp.</p>
                  </div>
                )}
              </div>

              <div className="space-y-4 min-w-0">
                <div className="rounded-lg border border-slate-200 p-4">
                  <span className="block text-sm text-slate-500 mb-1">Instancia</span>
                  <strong className="text-lg text-slate-900">{selectedInstance}</strong>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <span className="block text-sm text-slate-500 mb-1">Status consultado</span>
                  <p className="text-sm text-slate-800 break-words">{statusText || (selectedState ? getEvolutionConnectionLabel(selectedState) : 'Clique em Status para consultar.')}</p>
                </div>
                {pairingCode && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 min-w-0">
                    <span className="block text-sm text-emerald-700 mb-1">Codigo de pareamento</span>
                    <strong className="block text-xl tracking-wide text-emerald-900 break-all">{pairingCode}</strong>
                  </div>
                )}
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
                  {qrImage
                    ? 'Enquanto o QR Code estiver visivel, o sistema verifica automaticamente se a conexao foi concluida.'
                    : pairingCode
                      ? 'Enquanto o codigo de pareamento estiver visivel, o sistema verifica automaticamente se a conexao foi concluida.'
                      : 'Abra o WhatsApp no celular, entre em aparelhos conectados e leia o QR Code gerado pela Evolution API.'}
                </div>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex items-center gap-3 mb-3">
                <Phone className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-slate-900">Conectar com numero de telefone</h3>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Se o QR Code nao funcionar, gere um codigo de pareamento. No celular: WhatsApp &gt; Aparelhos conectados &gt; Conectar com numero de telefone, e digite o codigo mostrado aqui.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <input
                  value={pairingPhoneNumber}
                  onChange={(event) => setPairingPhoneNumber(event.target.value.replace(/\D/g, ''))}
                  className="input"
                  placeholder="Ex: 5533999999999 (DDI + DDD + numero)"
                />
                <button
                  onClick={connectWithPhoneNumber}
                  disabled={Boolean(loading)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {loading === 'connect-number' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                  Gerar codigo
                </button>
              </div>
            </div>
          </section>
        </div>
          </>
        )}

        {activeTab === 'tests' && (
          <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-emerald-600" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Teste de inscricao real</h2>
                  <p className="text-sm text-slate-500">Selecione uma inscricao ja feita e envie para o numero conectado nesta instancia.</p>
                </div>
              </div>
              <button
                onClick={loadContracts}
                disabled={Boolean(loading)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 disabled:opacity-60"
              >
                {loading === 'contracts' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Atualizar inscricoes
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Inscricao</label>
                  <select
                    value={selectedContractId}
                    onChange={(event) => setSelectedContractId(event.target.value)}
                    className="input"
                  >
                    {contracts.length === 0 && <option value="">Nenhuma inscricao encontrada</option>}
                    {contracts.map((contract) => (
                      <option key={contract.id} value={contract.id}>
                        {contract.contratante || 'Sem contratante'} - {contract.nomeAniversariante || 'Sem aniversariante'} - {contract.dataFesta || 'Sem data'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Numero de destino</label>
                  <input
                    value={testNumber}
                    onChange={(event) => setTestNumber(event.target.value)}
                    className="input"
                    placeholder={connectedInstanceNumber ? `Instancia conectada: ${connectedInstanceNumber}` : 'Opcional. Ex: 5533999999999'}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Se ficar vazio, o sistema usa o numero conectado na instancia selecionada.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Previa da mensagem</label>
                  <textarea
                    value={testMessage}
                    readOnly
                    rows={12}
                    className="input min-h-[260px] font-mono text-xs"
                    placeholder="Selecione uma inscricao para ver a mensagem."
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <span className="block text-sm text-slate-500 mb-1">Instancia</span>
                  <strong className="text-lg text-slate-900">{selectedInstance}</strong>
                  <p className="mt-2 text-sm text-slate-600">
                    Status: {selectedState ? getEvolutionConnectionLabel(selectedState) : 'nao encontrado'}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    Numero conectado: {connectedInstanceNumber || 'nao identificado'}
                  </p>
                </div>

                <button
                  onClick={sendTestContract}
                  disabled={Boolean(loading) || !selectedContract}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {loading === 'send-test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Enviar teste como nova inscricao
                </button>

                {testResult && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    {testResult}
                  </div>
                )}

                <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
                  Esse teste usa uma inscricao real existente, mas nao cria contrato novo e nao altera dados. Ele apenas envia a mensagem pela instancia Evolution.
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'settings' && (
          <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <Settings className="w-5 h-5 text-emerald-600" />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Configuracoes basicas</h2>
                <p className="text-sm text-slate-500">Controle o envio automatico quando o formulario publico for preenchido.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
              <div className="space-y-5">
                <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <div>
                    <span className="block text-sm font-semibold text-slate-900">Enviar mensagem automaticamente</span>
                    <span className="block text-sm text-slate-500">Quando uma inscricao nova for enviada pelo formulario, o sistema manda a mensagem pela instancia Evolution.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.enabled}
                    onChange={(event) => setNotificationSettings((current) => ({ ...current, enabled: event.target.checked }))}
                    className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </label>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Numeros que vao receber a mensagem</label>
                  <div className="space-y-2">
                    {notificationSettings.destinationNumbers.map((number, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          value={number}
                          onChange={(event) => updateDestinationNumber(index, event.target.value)}
                          className="input"
                          placeholder="Ex: 5533999999999"
                        />
                        <button
                          type="button"
                          onClick={() => removeDestinationNumber(index)}
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2.5 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                          title="Remover numero"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addDestinationNumber}
                    className="mt-2 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar numero
                  </button>
                  <p className="mt-2 text-xs text-slate-500">
                    Use DDI + DDD + numero (ex: 5533999999999). Toda nova inscricao e enviada para todos os numeros da lista.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Instancia usada no envio</label>
                  <select
                    value={notificationSettings.instanceName}
                    onChange={(event) => setNotificationSettings((current) => ({ ...current, instanceName: event.target.value }))}
                    className="input"
                  >
                    <option value={DEFAULT_INSTANCE}>{DEFAULT_INSTANCE}</option>
                    {instances.map((instance) => {
                      const name = getEvolutionInstanceName(instance);
                      return name ? <option key={name} value={name}>{name}</option> : null;
                    })}
                  </select>
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                    <span className="block text-slate-500">Status atual da instancia</span>
                    <strong className={notificationInstanceState && isEvolutionConnected(notificationInstanceState) ? 'text-emerald-700' : 'text-slate-900'}>
                      {notificationInstanceState ? getEvolutionConnectionLabel(notificationInstanceState) : 'nao encontrado'}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <span className="block text-sm text-slate-500 mb-1">Status atual</span>
                  <strong className={notificationSettings.enabled ? 'text-emerald-700' : 'text-slate-900'}>
                    {notificationSettings.enabled ? 'Envio automatico ativado' : 'Envio automatico desativado'}
                  </strong>
                  <p className="mt-2 text-sm text-slate-600">
                    {notificationSettings.destinationNumbers.filter(Boolean).length > 0
                      ? `Numeros destino (${notificationSettings.destinationNumbers.filter(Boolean).length}): ${notificationSettings.destinationNumbers.filter(Boolean).join(', ')}`
                      : 'Nenhum numero configurado'}
                  </p>
                </div>

                <button
                  onClick={saveNotificationSettings}
                  disabled={Boolean(loading)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {loading === 'save-settings' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar configuracoes
                </button>

                <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
                  Essas configuracoes ficam salvas no Firebase e valem para o formulario publico, mesmo quando a inscricao vier de outro aparelho.
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default WhatsAppConnectionPage;
